import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, FileCheck2, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import {
  EQUIPMENT,
  EXPERIENCE_LEVELS,
  CDL_CLASSES,
  EMPLOYMENT_TYPES,
  DAYS,
  TIMES,
} from "../lib/formConfig";

const inputCls =
  "w-full border border-gray-300 bg-white px-4 py-2.5 text-[15px] text-[#1a1a1a] rounded-md focus:border-[#00331D] focus:ring-1 focus:ring-[#00331D] focus:outline-none transition-colors placeholder:text-gray-400";
const labelCls = "block text-sm font-semibold text-[#1a1a1a] mb-1.5";

function Field({ label, required, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className={labelCls}>
        {label} {required && <span className="text-[#00331D]">*</span>}
      </label>
      {children}
    </div>
  );
}

function SectionTitle({ title }) {
  return (
    <h3 className="text-lg font-bold text-[#1a1a1a] pb-2 mb-5 border-b border-gray-200">{title}</h3>
  );
}

const emptyRef = { name: "", relationship: "", phone: "", email: "" };

export default function ApplicationForm() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resume, setResume] = useState(null);

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    street: "", city: "", state: "", zip_code: "",
    years_experience: "", employment_type: "",
    has_cdl: false, cdl_class: "", other_licenses: "",
    equipment_experience: [], message: "",
  });

  const [availability, setAvailability] = useState({
    days: [], time_of_day: [], hours_per_week: "", earliest_start: "", notes: "",
  });

  const [references, setReferences] = useState([{ ...emptyRef }, { ...emptyRef }]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAvail = (k, v) => setAvailability((a) => ({ ...a, [k]: v }));
  const toggleArr = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const needsAvailability = form.employment_type === "part_time" || form.employment_type === "both";

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/applications/upload-resume", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResume(res.data);
      toast.success("Resume uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const updateRef = (i, k, v) =>
    setReferences((refs) => refs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const submit = async (e) => {
    e.preventDefault();
    const req = ["first_name", "last_name", "email", "phone", "street", "city", "state", "zip_code", "years_experience", "employment_type"];
    for (const k of req) {
      if (!form[k]) { toast.error("Please complete all required fields."); return; }
    }
    if (form.equipment_experience.length === 0) {
      toast.error("Select at least one type of equipment you can operate."); return;
    }
    const validRefs = references.filter((r) => r.name && r.phone);
    if (validRefs.length < 2) {
      toast.error("Two references with name and phone are required."); return;
    }
    setSubmitting(true);
    try {
      await api.post("/applications", {
        ...form,
        availability: needsAvailability ? availability : null,
        references: validRefs,
        resume,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="application-success" className="bg-white border border-gray-200 rounded-lg shadow-sm p-10 text-center">
        <CheckCircle2 className="w-14 h-14 text-[#00331D] mx-auto mb-5" strokeWidth={1.5} />
        <h3 className="text-2xl font-bold text-[#1a1a1a]">Application Received</h3>
        <p className="mt-3 text-[#585858] max-w-lg mx-auto">
          Thank you for applying to Land ClearCo. Our team reviews every application and will reach out if there's a fit.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="application-form" className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 md:p-9 space-y-10">
      {/* Personal */}
      <section>
        <SectionTitle title="Your Information" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="First Name" required testid="field-first-name">
            <input className={inputCls} data-testid="input-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
          </Field>
          <Field label="Last Name" required testid="field-last-name">
            <input className={inputCls} data-testid="input-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
          </Field>
          <Field label="Email" required testid="field-email">
            <input type="email" className={inputCls} data-testid="input-email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </Field>
          <Field label="Phone" required testid="field-phone">
            <input className={inputCls} data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </Field>
        </div>
        <div className="mt-5">
          <Field label="Street Address" required testid="field-street">
            <input className={inputCls} data-testid="input-street" value={form.street} onChange={(e) => set("street", e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-5">
          <Field label="City" required testid="field-city">
            <input className={inputCls} data-testid="input-city" value={form.city} onChange={(e) => set("city", e.target.value)} />
          </Field>
          <Field label="State" required testid="field-state">
            <input className={inputCls} data-testid="input-state" value={form.state} onChange={(e) => set("state", e.target.value)} />
          </Field>
          <Field label="ZIP" required testid="field-zip">
            <input className={inputCls} data-testid="input-zip" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} />
          </Field>
        </div>
      </section>

      {/* Experience */}
      <section>
        <SectionTitle title="Equipment & Experience" />
        <Field label="Years Operating Heavy Equipment" required testid="field-years">
          <select className={inputCls} data-testid="input-years" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)}>
            <option value="">Select…</option>
            {EXPERIENCE_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </Field>

        <div className="mt-5">
          <Field label="Equipment You Can Operate" required testid="field-equipment">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {EQUIPMENT.map((eq) => {
                const active = form.equipment_experience.includes(eq.id);
                return (
                  <label key={eq.id} data-testid={`equipment-${eq.id}`} onClick={() => set("equipment_experience", toggleArr(form.equipment_experience, eq.id))}
                    className={`flex items-center gap-3 border rounded-md px-4 py-2.5 cursor-pointer transition-colors ${active ? "border-[#00331D] bg-[#00331D]/5" : "border-gray-300 hover:border-[#00331D]"}`}>
                    <span className={`w-4 h-4 rounded-sm border flex items-center justify-center ${active ? "bg-[#00331D] border-[#00331D]" : "border-gray-400"}`}>
                      {active && <CheckCircle2 className="w-3 h-3 text-white" />}
                    </span>
                    <span className="text-[15px] text-[#1a1a1a]">{eq.label}</span>
                  </label>
                );
              })}
            </div>
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
          <div>
            <label className={labelCls}>Commercial Driver's License (CDL)</label>
            <label className="flex items-center gap-3 border border-gray-300 rounded-md px-4 py-2.5 cursor-pointer" data-testid="field-has-cdl">
              <input type="checkbox" className="w-4 h-4 accent-[#00331D]" checked={form.has_cdl} onChange={(e) => set("has_cdl", e.target.checked)} data-testid="input-has-cdl" />
              <span className="text-[15px] text-[#1a1a1a]">I hold a valid CDL</span>
            </label>
          </div>
          {form.has_cdl && (
            <Field label="CDL Class" testid="field-cdl-class">
              <select className={inputCls} data-testid="input-cdl-class" value={form.cdl_class} onChange={(e) => set("cdl_class", e.target.value)}>
                <option value="">Select…</option>
                {CDL_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          )}
        </div>
        <div className="mt-5">
          <Field label="Other Licenses / Certifications" testid="field-other-licenses">
            <input className={inputCls} data-testid="input-other-licenses" placeholder="e.g. OSHA 10, DOT medical card" value={form.other_licenses} onChange={(e) => set("other_licenses", e.target.value)} />
          </Field>
        </div>
      </section>

      {/* Availability */}
      <section>
        <SectionTitle title="Availability" />
        <Field label="What are you looking for?" required testid="field-employment-type">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {EMPLOYMENT_TYPES.map((t) => {
              const active = form.employment_type === t.id;
              return (
                <button type="button" key={t.id} data-testid={`employment-${t.id}`} onClick={() => set("employment_type", t.id)}
                  className={`border rounded-md p-4 text-left transition-colors ${active ? "border-[#00331D] bg-[#00331D]/5" : "border-gray-300 hover:border-[#00331D]"}`}>
                  <div className="font-semibold text-[#1a1a1a]">{t.label}</div>
                  <div className="text-xs text-[#585858] mt-0.5">{t.desc}</div>
                </button>
              );
            })}
          </div>
        </Field>

        {needsAvailability && (
          <div className="mt-6 border-l-2 border-[#00331D] pl-5 space-y-5" data-testid="availability-block">
            <p className="text-sm font-semibold text-[#00331D]">Tell us when you can work</p>
            <Field label="Available Days" testid="field-avail-days">
              <div className="flex flex-wrap gap-2">
                {DAYS.map((d) => {
                  const active = availability.days.includes(d);
                  return (
                    <button type="button" key={d} data-testid={`day-${d}`} onClick={() => setAvail("days", toggleArr(availability.days, d))}
                      className={`px-3.5 py-1.5 rounded-md border text-sm font-medium transition-colors ${active ? "border-[#00331D] bg-[#00331D] text-white" : "border-gray-300 hover:border-[#00331D]"}`}>{d}</button>
                  );
                })}
              </div>
            </Field>
            <Field label="Preferred Times" testid="field-avail-times">
              <div className="flex flex-wrap gap-2">
                {TIMES.map((t) => {
                  const active = availability.time_of_day.includes(t);
                  return (
                    <button type="button" key={t} data-testid={`time-${t}`} onClick={() => setAvail("time_of_day", toggleArr(availability.time_of_day, t))}
                      className={`px-3.5 py-1.5 rounded-md border text-sm font-medium transition-colors ${active ? "border-[#00331D] bg-[#00331D] text-white" : "border-gray-300 hover:border-[#00331D]"}`}>{t}</button>
                  );
                })}
              </div>
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Hours / Week" testid="field-hours">
                <input className={inputCls} data-testid="input-hours" placeholder="e.g. 20-30" value={availability.hours_per_week} onChange={(e) => setAvail("hours_per_week", e.target.value)} />
              </Field>
              <Field label="Earliest Start Date" testid="field-start">
                <input type="date" className={inputCls} data-testid="input-start" value={availability.earliest_start} onChange={(e) => setAvail("earliest_start", e.target.value)} />
              </Field>
            </div>
            <Field label="Scheduling Notes" testid="field-avail-notes">
              <input className={inputCls} data-testid="input-avail-notes" placeholder="Anything about your schedule" value={availability.notes} onChange={(e) => setAvail("notes", e.target.value)} />
            </Field>
          </div>
        )}
      </section>

      {/* References */}
      <section>
        <SectionTitle title="References" />
        <p className="text-sm text-[#585858] -mt-3 mb-4">At least two professional references are required.</p>
        <div className="space-y-5">
          {references.map((r, i) => (
            <div key={i} className="border border-gray-200 rounded-md p-4 space-y-4" data-testid={`reference-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-[#00331D]">Reference {i + 1}{i < 2 && " *"}</span>
                {references.length > 2 && (
                  <button type="button" data-testid={`remove-reference-${i}`} onClick={() => setReferences((refs) => refs.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input className={inputCls} data-testid={`ref-name-${i}`} placeholder="Full name" value={r.name} onChange={(e) => updateRef(i, "name", e.target.value)} />
                <input className={inputCls} data-testid={`ref-relationship-${i}`} placeholder="Relationship (e.g. former supervisor)" value={r.relationship} onChange={(e) => updateRef(i, "relationship", e.target.value)} />
                <input className={inputCls} data-testid={`ref-phone-${i}`} placeholder="Phone" value={r.phone} onChange={(e) => updateRef(i, "phone", e.target.value)} />
                <input className={inputCls} data-testid={`ref-email-${i}`} placeholder="Email (optional)" value={r.email} onChange={(e) => updateRef(i, "email", e.target.value)} />
              </div>
            </div>
          ))}
        </div>
        <button type="button" data-testid="add-reference-btn" onClick={() => setReferences((refs) => [...refs, { ...emptyRef }])} className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#00331D] hover:text-[#004d2b]">
          <Plus className="w-4 h-4" /> Add another reference
        </button>
      </section>

      {/* Resume + message */}
      <section>
        <SectionTitle title="Resume & Notes" />
        <Field label="Upload Resume (PDF, DOC, DOCX — optional)" testid="field-resume">
          <label className={`flex items-center gap-3 border border-dashed rounded-md px-5 py-5 cursor-pointer transition-colors ${resume ? "border-[#00331D] bg-[#00331D]/5" : "border-gray-300 hover:border-[#00331D]"}`}>
            <input type="file" className="hidden" accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" onChange={handleUpload} data-testid="input-resume" />
            {uploading ? <Loader2 className="w-5 h-5 text-[#00331D] animate-spin" /> : resume ? <FileCheck2 className="w-5 h-5 text-[#00331D]" /> : <Upload className="w-5 h-5 text-gray-400" />}
            <span className="text-[15px] text-[#1a1a1a]">{uploading ? "Uploading…" : resume ? resume.original_filename : "Click to choose a file"}</span>
          </label>
        </Field>
        <div className="mt-5">
          <Field label="Anything else you'd like us to know?" testid="field-message">
            <textarea rows={4} className={inputCls} data-testid="input-message" value={form.message} onChange={(e) => set("message", e.target.value)} />
          </Field>
        </div>
      </section>

      <button type="submit" disabled={submitting} data-testid="submit-application-btn"
        className="w-full bg-[#00331D] text-white font-semibold uppercase tracking-wider px-8 py-4 rounded-md hover:bg-[#004d2b] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
        {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
        {submitting ? "Submitting…" : "Submit Application"}
      </button>
    </form>
  );
}
